import React, { useEffect, useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { deviceService } from '../../../services/deviceService';
import { programmingService, errorDetail, CallResult } from '../../../services/programmingService';
import { normalisePhone } from '../../navigation/GlobalSearch';

interface Props {
  deviceId: string;
  onSaved: () => void;
  onClose: () => void;
}

type Step = 'pinging' | 'offline' | 'online' | 'calling' | 'called' | 'saving' | 'error';

const CALL_STEPS: Step[] = ['online', 'calling', 'called', 'saving'];

/**
 * Recovers a missing line number in three steps: the gateway is pinged
 * over MQTT; when it answers, it is told to dial the environment's test
 * line (the support team's `call "number;line;seconds"` command); it then
 * publishes its own line number in a callerid event, offered here to be
 * saved. If it does not, the number shown on the test line's phone is
 * typed in instead.
 */
export const PhoneRecoveryPanel: React.FC<Props> = ({ deviceId, onSaved, onClose }) => {
  const { t } = useI18n();
  const [step, setStep] = useState<Step>('pinging');
  const [identification, setIdentification] = useState<string | null>(null);
  const [callResult, setCallResult] = useState<CallResult | null>(null);
  const [lineNumber, setLineNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  const ping = async () => {
    setStep('pinging');
    setError(null);
    try {
      const result = await programmingService.ping(deviceId);
      setIdentification(result.identification);
      setStep(result.online ? 'online' : 'offline');
    } catch (err) {
      setError(errorDetail(err));
      setStep('error');
    }
  };

  // The panel opens on a ping: no point showing the call to a silent gateway.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ping(); }, [deviceId]);

  const call = async () => {
    setStep('calling');
    setError(null);
    try {
      const result = await programmingService.call(deviceId);
      setCallResult(result);
      if (result.status === 'error') {
        setError(t('devices.phoneRecovery.refused', { detail: result.reply || '' }));
        setStep('online');
      } else {
        if (result.line_number) setLineNumber(result.line_number);
        setStep('called');
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = errorDetail(err);
      setError(status === 404 ? t('devices.phoneRecovery.notDeployed')
        : /CALL_PROBE_NUMBER/.test(detail) ? t('devices.phoneRecovery.notConfigured')
        : detail);
      setStep('online');
    }
  };

  const save = async () => {
    const value = normalisePhone(lineNumber);
    if (!/^[0-9]{8,15}$/.test(value)) {
      setError(t('devices.phoneRecovery.invalidLine'));
      return;
    }
    setStep('saving');
    setError(null);
    try {
      await deviceService.patchDevice(deviceId, { phone: value });
      onSaved();
    } catch (err) {
      setError(errorDetail(err));
      setStep('called');
    }
  };

  const connected = CALL_STEPS.includes(step);
  const called = step === 'called' || step === 'saving';
  // The identification is long (firmware, modem, cell…): the first token
  // says what matters, the rest is a tooltip.
  const [identShort, ...identRest] = (identification || '').split(' ');

  const stepClass = (done: boolean, active: boolean) =>
    `phone-recovery-step${done ? ' is-done' : ''}${active ? ' is-active' : ''}`;

  return (
    <section className="phone-recovery" data-testid="phone-recovery" aria-label={t('devices.phoneRecovery.title')}>
      <header className="phone-recovery-head">
        <div>
          <strong>{t('devices.phoneRecovery.title')}</strong>
          <span className="phone-recovery-sub">{t('devices.phoneRecovery.subtitle')}</span>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} data-testid="phone-recovery-close">
          {t('common.close')}
        </button>
      </header>

      {/* 1 — MQTT */}
      <div className={stepClass(connected, step === 'pinging' || step === 'offline' || step === 'error')}>
        <span className="phone-recovery-no" aria-hidden="true">{connected ? '✓' : '1'}</span>
        <div className="phone-recovery-body">
          <div className="phone-recovery-title">{t('devices.phoneRecovery.step1')}</div>
          {step === 'pinging' && <div className="phone-recovery-muted">{t('devices.programming.pinging')}</div>}
          {(step === 'offline' || step === 'error') && (
            <div className="phone-recovery-row">
              <span className="phone-recovery-bad">{step === 'offline' ? t('devices.phoneRecovery.offline') : error}</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={ping} data-testid="phone-recovery-retry">
                {t('devices.phoneRecovery.retry')}
              </button>
            </div>
          )}
          {connected && (
            <div className="phone-recovery-row">
              <span className="phone-recovery-pill is-ok">{t('devices.phoneRecovery.online')}</span>
              {identification && (
                <span className="phone-recovery-ident" title={identification}>
                  {identShort}{identRest.length > 0 ? ' …' : ''}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 2 — the test call */}
      <div className={stepClass(called, step === 'online' || step === 'calling')}>
        <span className="phone-recovery-no" aria-hidden="true">{called ? '✓' : '2'}</span>
        <div className="phone-recovery-body">
          <div className="phone-recovery-title">{t('devices.phoneRecovery.step2')}</div>
          {connected && (
            <>
              <div className="phone-recovery-muted">{t('devices.phoneRecovery.callHelp')}</div>
              <div className="phone-recovery-row">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={call}
                  disabled={step !== 'online'}
                  data-testid="phone-recovery-call"
                >
                  {step === 'calling' ? t('devices.phoneRecovery.calling') : called ? t('devices.phoneRecovery.callAgain') : t('devices.phoneRecovery.call')}
                </button>
                {callResult && called && (
                  <span className="phone-recovery-muted" data-testid="phone-recovery-called">
                    {t('devices.phoneRecovery.sentTo', { number: callResult.number, line: callResult.line, duration: callResult.duration })}
                    {' · '}
                    {callResult.line_number ? t('devices.phoneRecovery.detected') : t('devices.phoneRecovery.notDetected')}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 3 — the gateway's number */}
      <div className={stepClass(false, called)}>
        <span className="phone-recovery-no" aria-hidden="true">3</span>
        <div className="phone-recovery-body">
          <div className="phone-recovery-title">{t('devices.phoneRecovery.step3')}</div>
          {called && (
            <div className="phone-recovery-row">
              <label className="phone-recovery-field">
                <span>{t('devices.phoneRecovery.lineNumber')}</span>
                <input
                  type="tel"
                  className="field-input monospace"
                  value={lineNumber}
                  onChange={(e) => setLineNumber(e.target.value)}
                  placeholder="33612345678"
                  disabled={step === 'saving'}
                  data-testid="phone-recovery-line-number"
                />
                <small className="phone-recovery-muted">
                  {callResult?.line_number ? t('devices.phoneRecovery.lineNumberFromGateway') : t('devices.phoneRecovery.lineNumberFromPhone')}
                </small>
              </label>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={save}
                disabled={step === 'saving' || !lineNumber.trim()}
                data-testid="phone-recovery-save"
              >
                {step === 'saving' ? t('devices.saving') : t('devices.phoneRecovery.save')}
              </button>
            </div>
          )}
        </div>
      </div>

      {error && step !== 'error' && <p className="phone-recovery-bad phone-recovery-alert" role="alert">{error}</p>}
    </section>
  );
};

export default PhoneRecoveryPanel;
